import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  FileText,
  Upload,
  X,
  Loader2,
  Sparkles,
  Wand2,
} from 'lucide-react';

/**
 * UploadModal - Standard document upload modal with metadata fields
 */
const UploadModal = ({
  isOpen,
  onClose,
  uploadName,
  setUploadName,
  uploadFile,
  setUploadFile,
  uploadMetadata,
  setUploadMetadata,
  autoClassifyEnabled,
  setAutoClassifyEnabled,
  autoClassifying,
  autoClassifyConfidence,
  setAutoClassifyConfidence,
  documentTypes,
  suggestedDocTypes = [],
  handleDocumentTypeChange,
  handleFileSelect,
  handleUpload,
  uploading,
  resetUploadForm,
}) => {
  // Get sorted document types with suggested ones first
  const getSortedDocTypes = () => {
    if (!documentTypes.length) return [];
    if (!suggestedDocTypes.length) return [...documentTypes].sort();
    
    const suggested = [];
    const others = [];
    
    documentTypes.forEach(type => {
      if (suggestedDocTypes.includes(type)) {
        suggested.push(type);
      } else {
        others.push(type);
      }
    });
    
    return [
      ...suggested.sort((a, b) => suggestedDocTypes.indexOf(a) - suggestedDocTypes.indexOf(b)),
      ...others.sort()
    ];
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-slate-900 dark:text-slate-100">Upload Document</DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400">
            Fill in the required fields (*) to properly file and index the document.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Document Name *</Label>
              <Input
                placeholder="Enter document name"
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Document Type *</Label>
              <Select value={uploadMetadata.document_type} onValueChange={handleDocumentTypeChange}>
                <SelectTrigger className={`bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 ${uploadMetadata.document_type ? 'border-green-500/50' : ''}`}>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 max-h-60">
                  {suggestedDocTypes.length > 0 && (
                    <div className="px-2 py-1.5 text-xs font-semibold text-purple-600 dark:text-purple-400 border-b border-slate-200 dark:border-slate-700">
                      <Sparkles className="w-3 h-3 inline mr-1" />
                      Matching types based on filename
                    </div>
                  )}
                  {getSortedDocTypes().map((type, index) => {
                    const isSuggested = suggestedDocTypes.includes(type);
                    const isFirstNonSuggested = suggestedDocTypes.length > 0 && !isSuggested && 
                      (index === 0 || suggestedDocTypes.includes(getSortedDocTypes()[index - 1]));
                    return (
                      <React.Fragment key={type}>
                        {isFirstNonSuggested && (
                          <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 border-t border-slate-200 dark:border-slate-700 mt-1">
                            All document types
                          </div>
                        )}
                        <SelectItem 
                          value={type} 
                          className={`${isSuggested 
                            ? 'text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 font-medium' 
                            : 'text-gray-700 dark:text-slate-300'} hover:bg-slate-200 dark:hover:bg-slate-700`}
                        >
                          <span className="flex items-center gap-2">
                            {isSuggested && <Sparkles className="w-3 h-3 text-purple-500" />}
                            {type}
                          </span>
                        </SelectItem>
                      </React.Fragment>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-slate-700 dark:text-slate-300">File *</Label>
              <div className="flex items-center gap-2">
                <Label htmlFor="auto-classify" className="text-xs text-slate-600 dark:text-slate-400 cursor-pointer flex items-center gap-1.5">
                  <Wand2 className="w-3.5 h-3.5" />
                  Auto-classify
                </Label>
                <Switch
                  id="auto-classify"
                  checked={autoClassifyEnabled}
                  onCheckedChange={setAutoClassifyEnabled}
                  className="data-[state=checked]:bg-red-600"
                />
              </div>
            </div>
            <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6 text-center bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-100 dark:bg-slate-800 transition-colors">
              {uploadFile ? (
                <div className="flex items-center justify-center gap-2">
                  <FileText className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{uploadFile.name}</span>
                  {autoClassifying && <Loader2 className="w-4 h-4 animate-spin text-red-600" />}
                  {autoClassifyConfidence > 0 && !autoClassifying && (
                    <Badge variant="outline" className={`text-xs ${autoClassifyConfidence > 0.5 ? 'border-green-500 text-green-600' : 'border-yellow-500 text-yellow-400'}`}>
                      <Sparkles className="w-3 h-3 mr-1" />
                      {Math.round(autoClassifyConfidence * 100)}%
                    </Badge>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => { setUploadFile(null); setAutoClassifyConfidence(0); }} className="h-6 w-6 p-0 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-100">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-slate-500 dark:text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600 dark:text-slate-400">Click to select a file or drag and drop</p>
                  {autoClassifyEnabled && (
                    <p className="text-xs text-red-600 mt-1 flex items-center justify-center gap-1">
                      <Wand2 className="w-3 h-3" />
                      Auto-classification enabled
                    </p>
                  )}
                  <input type="file" className="hidden" onChange={(e) => handleFileSelect(e.target.files[0])} />
                </label>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Document Date *</Label>
              <Input type="date" value={uploadMetadata.document_date} onChange={(e) => setUploadMetadata(prev => ({ ...prev, document_date: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Effective Date</Label>
              <Input type="date" value={uploadMetadata.effective_date} onChange={(e) => setUploadMetadata(prev => ({ ...prev, effective_date: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Filing Date</Label>
              <Input type="date" value={uploadMetadata.filing_date} onChange={(e) => setUploadMetadata(prev => ({ ...prev, filing_date: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Receipt Date</Label>
              <Input type="date" value={uploadMetadata.receipt_date} onChange={(e) => setUploadMetadata(prev => ({ ...prev, receipt_date: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Site</Label>
              <Input placeholder="Site name or ID" value={uploadMetadata.site} onChange={(e) => setUploadMetadata(prev => ({ ...prev, site: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Country</Label>
              <Input placeholder="Country" value={uploadMetadata.country} onChange={(e) => setUploadMetadata(prev => ({ ...prev, country: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Owner</Label>
              <Input placeholder="Document owner" value={uploadMetadata.owner} onChange={(e) => setUploadMetadata(prev => ({ ...prev, owner: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Contact</Label>
              <Input placeholder="Contact person" value={uploadMetadata.contact} onChange={(e) => setUploadMetadata(prev => ({ ...prev, contact: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 dark:text-slate-300">Artifact Classification (Binder/Section)</Label>
            <Input 
              placeholder="Auto-generated from folder path and document type" 
              value={uploadMetadata.artifact_classification} 
              onChange={(e) => setUploadMetadata(prev => ({ ...prev, artifact_classification: e.target.value }))}
              className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">Auto-populated based on folder path and document type. You can edit if needed.</p>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 dark:text-slate-300">Keywords (comma-separated)</Label>
            <Input placeholder="e.g., protocol, amendment, v2" value={uploadMetadata.keywords} onChange={(e) => setUploadMetadata(prev => ({ ...prev, keywords: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 dark:text-slate-300">Description</Label>
            <Input placeholder="Brief description of the document" value={uploadMetadata.description} onChange={(e) => setUploadMetadata(prev => ({ ...prev, description: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onClose(false); resetUploadForm(); }} className="rounded-full bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-100">
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={uploading} className="rounded-full bg-red-600 hover:bg-red-700 text-slate-900 dark:text-slate-100">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload & File'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadModal;
